// Text rules shared by the /chat page and the athlete scheda's chat panel (ChatParts):
// what a typed message becomes before it is stored, and how a conversation's last
// message and time read in the sidebar.

// Plain text in, escaped first (message.content is rendered as HTML), then links.
export const escapeHtml = text =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/\n/g, '<br>');

export const convertLinksInText = text =>
  text.replace(
    /(https?:\/\/[^\s<>"]+)/g,
    url => `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color: #E6616A; text-decoration: underline;">${url}</a>`
  );

/** What a typed draft is stored as. */
export const messageHtml = draft => convertLinksInText(escapeHtml(draft.trim()));

export const fmtTime = ts => {
  if (!ts) return '';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/** A sidebar time: the hour today, the day otherwise. */
export const listTime = (ts, now = Date.now()) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date(now);
  if (d.toDateString() === today.toDateString()) return fmtTime(ts);
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit' });
};

/** HTML message content as one line of plain text. */
export const plainText = html =>
  String(html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

/** A conversation's last message for the sidebar: "You: …" when mine, a label for media. */
export const previewOf = (message, userId, t) => {
  if (!message) return '';
  const body =
    message.type === 'image'
      ? `📷 ${t('CHAT.photo', 'Photo')}`
      : message.type === 'audio'
      ? `🎤 ${t('CHAT.audio', 'Audio')}`
      : message.type === 'file'
      ? `📄 ${t('CHAT.pdf', 'PDF')}`
      : message.type === 'video'
      ? `🎥 ${t('CHAT.video', 'Video')}`
      : plainText(message.content);
  const sender = message.senderId ?? message.user?.id;
  return String(sender) === String(userId) ? `${t('CHAT.you', 'You')}: ${body}` : body;
};

/** The conversations whose name (or a member's email) contains `term`. */
export const filterConversations = (rows, term) => {
  const search = term.trim().toLowerCase();
  if (!search) return rows;
  return rows.filter(row =>
    [row.name, row.email].some(value =>
      String(value || '')
        .toLowerCase()
        .includes(search)
    )
  );
};

/** Up to two initials of a full name ("Giulio Rossi" → "GR"). */
export const nameInitials = name =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('') || '?';

/** A contact is a customer of the centre or one of its collaborators (staff). */
export const contactKind = contact => (contact?.role === 'customer' ? 'customer' : 'collaborator');

/**
 * The contacts to start a chat with: one row per person (someone who is both a
 * customer and a collaborator shows once, as a collaborator), never me.
 */
export const chatContacts = (contacts, userId) => {
  const byId = new Map();
  (contacts || []).forEach(contact => {
    if (!contact?.id || String(contact.id) === String(userId)) return;
    const seen = byId.get(contact.id);
    if (!seen || (contactKind(seen) === 'customer' && contactKind(contact) === 'collaborator')) byId.set(contact.id, contact);
  });
  return [...byId.values()].sort((a, b) => String(a.name || a.email || '').localeCompare(String(b.name || b.email || '')));
};

/** The first link in a message (stored as HTML with <a href>): { href, host, path } for its card, or null. */
export const linkPreview = html => {
  const match = String(html || '').match(/href="(https?:\/\/[^"]+)"/i) || plainText(html).match(/https?:\/\/[^\s<>"]+/i);
  if (!match) return null;
  try {
    const url = new URL(match[1] || match[0]);
    const path = `${url.pathname === '/' ? '' : url.pathname}${url.search}`;
    return { href: url.href, host: url.hostname.replace(/^www\./, ''), path: path.length > 60 ? `${path.slice(0, 59)}…` : path };
  } catch {
    return null;
  }
};

/** A voice note's bars when it has no saved levels: the same for the same id every time, 0.25–1. Same as the app's. */

export const waveformBars = (seed, count = 36) => {
  let h = 2166136261;
  for (const ch of String(seed || 'voice')) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return Array.from({ length: count }, (_, i) => {
    h = Math.imul(h ^ (i + 1), 2654435761) >>> 0;
    const wave = 0.55 + 0.45 * Math.sin(i * 0.7 + (h % 7));
    return Math.max(0.25, Math.min(1, wave * (0.6 + (h % 1000) / 2500)));
  });
};

/**
 * Recorded levels (0–1, any number) as `count` bars, scaled so the loudest bar is
 * full height: fewer bars keep the loudest of each slice, more bars are drawn in
 * between. No levels → the seeded bars.
 */
export const resampleLevels = (levels, count = 36, seed = 'voice') => {
  const values = (levels || []).map(Number).filter(Number.isFinite);
  if (!values.length) return waveformBars(seed, count);
  const peak = Math.max(...values) || 1;
  return Array.from({ length: count }, (_, i) => {
    let level;
    if (count <= values.length) {
      const from = Math.floor((i * values.length) / count);
      const to = Math.max(from + 1, Math.floor(((i + 1) * values.length) / count));
      level = Math.max(...values.slice(from, to));
    } else {
      const at = (i * (values.length - 1)) / Math.max(1, count - 1);
      const low = Math.floor(at);
      const high = Math.min(values.length - 1, low + 1);
      level = values[low] + (values[high] - values[low]) * (at - low);
    }
    return Math.max(0.15, Math.min(1, level / peak));
  });
};
